import {notificationFeedService} from "../services";
import {useNotificationFeedStore} from "../store";

function readPayloadString(
  payload: Record<string, unknown>,
  key: string,
): string | undefined {
  const value = payload[key];
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

/** Mark inbox row read when OS notification tap includes `notification_id` in push data. */
export async function markNotificationReadFromPushData(
  data: Record<string, unknown>,
): Promise<void> {
  const notificationId = readPayloadString(data, "notification_id");
  if (!notificationId) {
    return;
  }

  useNotificationFeedStore.getState().markReadLocal([notificationId]);

  try {
    await notificationFeedService.markReadByIds([notificationId]);
  } catch {
    // Still navigate if mark-read fails.
  }
}

export const PUSH_CLICK_QUERY_KEYS = [
  "_push",
  "push_type",
  "property_id",
  "notification_id",
  "shift_break_id",
  "shift_id",
] as const;

function readSearchParamNumber(searchParams: URLSearchParams, key: string): number | undefined {
  const raw = searchParams.get(key);
  if (!raw) {
    return undefined;
  }
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : undefined;
}

/** Rebuild push `data` from cold-start URL params (see `attachPushClickParams` in SW). */
export function pushNotificationDataFromSearchParams(
  searchParams: URLSearchParams,
): Record<string, unknown> | null {
  if (searchParams.get("_push") !== "1") {
    return null;
  }

  const data: Record<string, unknown> = {};
  const type = searchParams.get("push_type");
  if (type) {
    data.type = type;
  }

  const propertyId = readSearchParamNumber(searchParams, "property_id");
  if (propertyId != null) {
    data.property_id = propertyId;
  }

  const notificationId = searchParams.get("notification_id");
  if (notificationId) {
    data.notification_id = notificationId;
  }

  const shiftBreakId = readSearchParamNumber(searchParams, "shift_break_id");
  if (shiftBreakId != null) {
    data.shift_break_id = shiftBreakId;
  }

  const shiftId = readSearchParamNumber(searchParams, "shift_id");
  if (shiftId != null) {
    data.shift_id = shiftId;
  }

  return data;
}

export function stripPushClickSearchParams(searchParams: URLSearchParams): string {
  const next = new URLSearchParams(searchParams);
  for (const key of PUSH_CLICK_QUERY_KEYS) {
    next.delete(key);
  }
  const qs = next.toString();
  return qs ? `?${qs}` : "";
}
